import { useEffect, useState, useCallback } from 'react';
import { getClubs, eliminarClub } from '../../../services/clubsService';
import ClubFormModal from './ClubFormModal';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import AdminModal from '../components/AdminModal';
import ConfirmModal from '../components/ConfirmModal';
import AdminPagination from '../components/AdminPagination';

const badgeStyle = (estado) => {
  if (estado === 'activo') return theme.badgeActivo;
  if (estado === 'inactivo') return theme.badgeInactivo;
  return theme.badgeSuspendido;
};

const ClubsView = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clubEdit, setClubEdit] = useState(null);
  const [clubView, setClubView] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Paginación y búsqueda
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const cargarClubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getClubs(page, 10, search);
      setClubs(data.items || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      setError(err.message || 'Error al obtener clubs');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    cargarClubs();
  }, [cargarClubs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      setPage(prev => prev - 1);
    }
  };

  const handleEliminarClub = (id) => setConfirmDeleteId(id);

  const handleConfirmEliminar = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await eliminarClub(confirmDeleteId);
      setConfirmDeleteId(null);
      cargarClubs();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <h2 style={theme.title}>Clubes afiliados a ACHIPO</h2>
        <form onSubmit={handleSearch} style={theme.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por nombre, RUT o ciudad..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={theme.searchInput}
            aria-label="Buscar clubs"
          />
          <button type="submit" style={theme.btnGhost}>Buscar</button>
          {search && (
            <button type="button" style={theme.btnGhost} onClick={handleClearSearch}>
              Limpiar
            </button>
          )}
        </form>
        <button
          style={theme.btnPrimary}
          onClick={() => { setClubEdit(null); setShowModal(true); }}
          aria-label="Crear club"
        >
          Crear Club
        </button>
      </div>

      {error && <div style={theme.error}>{error}</div>}
      {loading && <AdminSpinner label="Cargando clubs…" />}

      {!loading && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>Nombre</th>
                  <th style={theme.th}>Ciudad</th>
                  <th style={theme.th}>Estado</th>
                  <th style={{ ...theme.th, textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clubs.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ ...theme.td, textAlign: 'center', color: '#666' }}>
                      {search ? `No se encontraron clubs para "${search}"` : 'No hay clubs registrados'}
                    </td>
                  </tr>
                ) : (
                  clubs.map((club) => (
                    <tr key={club.id}>
                      <td style={theme.td}>{club.nombre}</td>
                      <td style={theme.td}>{club.ciudad || '—'}</td>
                      <td style={theme.td}>
                        <span style={{ ...theme.badge, ...badgeStyle(club.estado) }}>{club.estado}</span>
                      </td>
                      <td style={{ ...theme.td, textAlign: 'right' }}>
                        <div style={theme.btnRow}>
                          <button style={theme.btnGhost} onClick={() => setClubView(club)} aria-label={`Ver ${club.nombre}`}>Ver</button>
                          <button style={theme.btnGhost} onClick={() => { setClubEdit(club); setShowModal(true); }} aria-label={`Editar ${club.nombre}`}>Editar</button>
                          <button style={theme.btnDanger} onClick={() => handleEliminarClub(club.id)} aria-label={`Eliminar ${club.nombre}`}>Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {clubs.length > 0 && (
            <AdminPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              pageSize={pagination.limit}
              hasPrev={pagination.has_prev}
              hasNext={pagination.has_next}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              label="clubs"
            />
          )}
        </>
      )}

      <AdminModal open={!!clubView} onClose={() => setClubView(null)} title="Detalle del club">
        {clubView && (
          <>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Nombre:</span> {clubView.nombre}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>RUT:</span> {clubView.rut_club || '—'}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Ciudad:</span> {clubView.ciudad || '—'}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Región:</span> {clubView.region || '—'}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Email:</span> {clubView.email || '—'}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Teléfono:</span> {clubView.telefono || '—'}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Miembros activos:</span> {clubView.miembros}</div>
            <div style={theme.modalRow}><span style={theme.modalLabel}>Gimnasios activos:</span> {clubView.gimnasios}</div>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button style={theme.btnGhost} onClick={() => setClubView(null)}>Cerrar</button>
            </div>
          </>
        )}
      </AdminModal>

      <ConfirmModal
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmEliminar}
        title="Eliminar club"
        message="¿Eliminar este club? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />

      {showModal && (
        <ClubFormModal club={clubEdit} onClose={() => setShowModal(false)} onSuccess={cargarClubs} />
      )}
    </div>
  );
};

export default ClubsView;
